import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import './App.css';
import { Case, CaseRequest } from './types/Case';
import { caseApi, caseRequestApi } from './services/api';
import * as tokenApi from './api/tokenApi';
import { showErrorNotification, showSuccessNotification } from './constants/uiConstants';
import { logApp } from './utils/logger';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './components/ErrorBoundary';
import { ModalProvider, useModalContext } from './contexts/ModalContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { getDocumentTitle } from './utils/environment';

// Lazy load components for code splitting
const CaseLog = lazy(() => import('./components/CaseLog'));
const CaseRequestModal = lazy(() => import('./components/CaseRequestModal'));
const TokenGenerationModal = lazy(() => import('./components/TokenGenerationModal'));
const ExternalCaseRequestForm = lazy(() => import('./components/ExternalCaseRequestForm'));
const CaseEditPage = lazy(() => import('./pages/CaseEditPage'));
const OpoAdminPage = lazy(() => import('./pages/OpoAdminPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));

// Hoisted main app content to avoid remounting across App state updates
type MainAppProps = {
  cases: Case[];
  caseRequests: CaseRequest[];
  loading: boolean;
  error: string | null;
  showCaseRequestForm: boolean;
  setShowCaseRequestForm: (open: boolean) => void;
  showTokenGenerationModal: boolean;
  setShowTokenGenerationModal: (open: boolean) => void;
  onCaseUpdate: (c: Case) => void;
  onCaseDelete: (id: number) => void;
  onCaseCreate: (c: Case) => void;
  onCaseRequestSave: (r: CaseRequest) => void;
  onTokenGeneration: (emails: string[], expirationHours: number) => Promise<void> | void;
  onRefreshCaseRequests: () => void | Promise<void>;
  caseRequestsRefreshToken: number;
};

const MainAppContent: React.FC<MainAppProps> = ({
  cases,
  caseRequests,
  loading,
  error,
  showCaseRequestForm,
  setShowCaseRequestForm,
  showTokenGenerationModal,
  setShowTokenGenerationModal,
  onCaseUpdate,
  onCaseDelete,
  onCaseCreate,
  onCaseRequestSave,
  onTokenGeneration,
  onRefreshCaseRequests,
  caseRequestsRefreshToken,
}) => {
  const { onModalOpen, onModalClose, pendingCaseRequestRefreshRef } = useModalContext();
  const [newRequestDraft, setNewRequestDraft] = useState<{ id: number; token: string } | null>(null);

  // Custom modal close handler that also handles pending refreshes
  const handleModalClose = () => {
    onModalClose();
    // If a case request was created while modal was open, trigger refresh now
    if (pendingCaseRequestRefreshRef.current) {
      pendingCaseRequestRefreshRef.current = false;
      // Notify parent to refresh via the caseRequestsRefreshToken increment
      // This is handled by calling onRefreshCaseRequests which updates the token
      setTimeout(() => onRefreshCaseRequests(), 200);
    }
  };

  // Debug
  logApp.debug('component state', {
    cases,
    caseRequests,
    loading,
    error,
    showCaseRequestForm,
    showTokenGenerationModal,
  });

  if (loading) {
    return (
      <div className="App min-h-screen bg-gray-100 flex items-center justify-center">
        <LoadingSpinner size="lg" message="Loading..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="App min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>

      <div id="main-content" className="App min-h-screen bg-gray-100">
        {/* Page Content */}
        <CaseLog
        cases={cases}
        refreshCaseRequestsSignal={caseRequestsRefreshToken}
        onCaseUpdate={onCaseUpdate}
        onCaseCreate={onCaseCreate}
        onCaseDelete={onCaseDelete}
        onModalOpen={() => {
          logApp.debug('onModalOpen()');
          onModalOpen();
        }}
        onModalClose={() => {
          logApp.debug('onModalClose()');
          handleModalClose();
        }}
        onCreateCaseRequest={async () => {
          // Create draft + token first, then open form with link populated
          try {
            const res = await tokenApi.createDraftWithToken(undefined, 72);
            setNewRequestDraft({ id: res.caseRequestId, token: res.token });
            // Immediately refresh the Case Requests table so the new draft appears under the modal
            try {
              await onRefreshCaseRequests();
            } catch (refreshError) {
              logApp.error('Failed to refresh case requests after creating draft:', refreshError);
              // Non-critical error, continue with form display
            }
            setShowCaseRequestForm(true);
            onModalOpen();
          } catch (e) {
            showErrorNotification('Failed to start new case request. Please try again.');
          }
        }}
      />

      {/* Token Generation Modal (unused in current flow but kept for future) */}
      <ErrorBoundary>
        <TokenGenerationModal
          isOpen={showTokenGenerationModal}
          onClose={() => {
            setShowTokenGenerationModal(false);
            handleModalClose();
          }}
          onGenerate={onTokenGeneration}
        />
      </ErrorBoundary>

      {/* Case Request Modal - Create Mode */}
      <ErrorBoundary>
        <CaseRequestModal
          mode="create"
          isOpen={showCaseRequestForm}
          onClose={() => {
            setShowCaseRequestForm(false);
            handleModalClose();
            setNewRequestDraft(null);
          }}
          onSave={onCaseRequestSave}
          onCaseCreated={onCaseCreate}
          initialDraft={newRequestDraft ? { id: newRequestDraft.id, token: newRequestDraft.token } : undefined}
        />
      </ErrorBoundary>
      </div>
    </>
  );
};

function AppContent() {
  const { isModalOpenRef, pendingCaseRequestRefreshRef } = useModalContext();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [cases, setCases] = useState<Case[]>([]);
  const [caseRequests, setCaseRequests] = useState<CaseRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaseRequestForm, setShowCaseRequestForm] = useState(false);
  const [showTokenGenerationModal, setShowTokenGenerationModal] = useState(false);
  const [caseRequestsRefreshToken, setCaseRequestsRefreshToken] = useState(0);

  // Refs to track state for focus handler (avoids stale closures)
  const errorRef = useRef<string | null>(null);
  const casesRef = useRef<Case[]>([]);

  // Keep refs in sync with state
  useEffect(() => { errorRef.current = error; }, [error]);
  useEffect(() => { casesRef.current = cases; }, [cases]);

  // Function to refresh data from the server
  const refreshData = async (isRetry = false) => {
    // Skip refresh if modal is open or not authenticated
    if (isModalOpenRef.current || !isAuthenticated) {
      return;
    }

    try {
      // Clear error state when retrying after a failure
      if (isRetry) {
        logApp.info('Retrying data fetch after previous failure');
        setError(null);
        setLoading(true);
      }

      // Call sequentially to avoid multiple authentication popups
      // Load recent cases for Case Log screen (uses client-side filtering)
      const casesResponse = await caseApi.getAllCases({ page: 1, pageSize: 1000 });
      const caseRequestsData = await caseRequestApi.getAllCaseRequests();

      // Extract data from paginated response
      const casesData = casesResponse.data || casesResponse;

      logApp.debug('Loaded cases', {
        count: casesData.length,
        totalCount: casesResponse.pagination?.totalCount,
        firstFew: casesData.slice(0, 3).map(c => ({ id: c.id, unosId: c.unosId, date: c.date }))
      });
      setCases(casesData);
      setCaseRequests(caseRequestsData);

      // Clear any previous error on successful load
      if (error) {
        setError(null);
      }
    } catch (err) {
      logApp.error('Error fetching data:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      if (isRetry) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Initial data fetch - only if authenticated
    const fetchData = async () => {
      // Don't fetch if still checking auth or not authenticated
      if (authLoading || !isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Add a small delay to ensure MSAL token cache is ready after login
        // This helps prevent 401 errors on the first API call after authentication
        await new Promise(resolve => setTimeout(resolve, 100));
        await refreshData();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, authLoading]);

  // Add window focus listener to refresh data when user returns to the app
  useEffect(() => {
    const handleWindowFocus = () => {
      // Only refresh on focus if no modals are open
      if (!isModalOpenRef.current) {
        // If there was an error or no cases loaded, treat this as a retry
        // This helps recover from cold start failures (uses refs for current values)
        const needsRetry = errorRef.current !== null || casesRef.current.length === 0;
        refreshData(needsRetry);
      }
    };

    // Handle visibility change (tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isModalOpenRef.current) {
        // If there was an error or no cases loaded, retry when tab becomes visible
        const needsRetry = errorRef.current !== null || casesRef.current.length === 0;
        if (needsRetry) {
          logApp.info('Tab became visible, retrying failed data load');
          refreshData(true);
        }
      }
    };

    // Check for case updates signal from CaseEditPage
    const checkForUpdates = () => {
      const lastUpdate = localStorage.getItem('caseflow.caseUpdated');
      if (lastUpdate && !isModalOpenRef.current) {
        // Clear the signal and refresh
        localStorage.removeItem('caseflow.caseUpdated');
        refreshData();
      }
    };

    // Check on mount and when window gains focus
    checkForUpdates();
    window.addEventListener('focus', () => {
      handleWindowFocus();
      checkForUpdates();
    });

    // Also listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also listen for storage events (for cross-tab updates)
    window.addEventListener('storage', checkForUpdates);

    // Check periodically while app is visible
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        checkForUpdates();
      }
    }, 1000);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', checkForUpdates);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - refreshData uses ref to avoid re-creation

  // Function to update a case in the local state
  const updateCase = (updatedCase: Case) => {
    setCases(prevCases => 
      prevCases.map(caseItem => 
        caseItem.id === updatedCase.id ? updatedCase : caseItem
      )
    );
  };

  // Function to remove a case from the local state
  const removeCase = (caseId: number) => {
    setCases(prevCases => prevCases.filter(caseItem => caseItem.id !== caseId));
  };

  // Function to add a new case to the local state
  const addCase = async (newCase: Case) => {
    // Add immediately to local state for instant feedback
    setCases(prevCases => [...prevCases, newCase]);

    // Refresh the full cases list from server to ensure searchability
    // Small delay to ensure backend has fully committed the transaction
    setTimeout(async () => {
      try {
        const casesResponse = await caseApi.getAllCases({ page: 1, pageSize: 1000 });
        const casesData = casesResponse.data || casesResponse;
        setCases(casesData);
        logApp.debug('Refreshed cases after new case creation', { newCaseId: newCase.id });
      } catch (err) {
        logApp.error('Failed to refresh cases after creation:', err);
        // Non-critical error - local state already has the new case
      }
    }, 500);
  };

  // Function to handle case request save
  const handleCaseRequestSave = (_request: CaseRequest) => {
    // Mark that we need to refresh after the modal closes
    pendingCaseRequestRefreshRef.current = true;
    // If no modal is open (edge case), refresh shortly
    setTimeout(() => { if (!isModalOpenRef.current) { pendingCaseRequestRefreshRef.current = false; refreshData(); } }, 250);
  };

  // Function to handle token generation
  const handleTokenGeneration = async (emails: string[], expirationHours: number) => {
    try {
      // Generate tokens for each email
      const tokenPromises = emails.map(email => 
        tokenApi.createToken({ coordinatorEmail: email, expiresIn: expirationHours })
      );
      
      const tokens = await Promise.all(tokenPromises);
      
      // Create access URLs for each token
      const accessUrls = tokens.map(tokenResult => 
        `${window.location.origin}/external-form/${tokenResult.token}`
      );
      
  // Display success message with URLs to share
  const urlList = accessUrls.map(u => `• ${u}`).join('\n');
  const message = `Token(s) generated successfully. Share these links with coordinators:\n${urlList}`;
  showSuccessNotification(message);

      setShowTokenGenerationModal(false);
      // Modal state is tracked via ref, updated by onClose callback
    } catch (error) {
  logApp.error('Token generation error:', error);
  showErrorNotification('Failed to generate token(s). Please try again.');
    }
  };

  // (removed old inline MainApp component; replaced by hoisted MainAppContent above)

  return (
    <ErrorBoundary
      fullPage
      onError={(error, info) => {
        logApp.error('App-level error caught:', error, info);
      }}
    >
      <Router>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><LoadingSpinner /></div>}>
          <Routes>
            {/* Main authenticated app route */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <MainAppContent
                      cases={cases}
                      caseRequests={caseRequests}
                      loading={loading}
                      error={error}
                      showCaseRequestForm={showCaseRequestForm}
                      setShowCaseRequestForm={setShowCaseRequestForm}
                      showTokenGenerationModal={showTokenGenerationModal}
                      setShowTokenGenerationModal={setShowTokenGenerationModal}
                      onCaseUpdate={updateCase}
                      onCaseCreate={addCase}
                      onCaseDelete={removeCase}
                      onCaseRequestSave={handleCaseRequestSave}
                      onTokenGeneration={handleTokenGeneration}
                      onRefreshCaseRequests={async () => {
                        if (isModalOpenRef.current) return; // keep behavior consistent
                        try {
                          const list = await caseRequestApi.getAllCaseRequests();
                          setCaseRequests(list);
                          // notify CaseLog to refresh its own list as well
                          setCaseRequestsRefreshToken(prev => prev + 1);
                        } catch (err) {
                          logApp.error('Refresh Case Requests failed:', err);
                        }
                      }}
                      caseRequestsRefreshToken={caseRequestsRefreshToken}
                    />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Case edit page route */}
            <Route
              path="/case/:id/edit"
              element={
                <ProtectedRoute>
                  <ErrorBoundary>
                    <CaseEditPage />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* OPO admin page route */}
            <Route
              path="/admin/opos"
              element={
                <ProtectedRoute requireAdmin={true}>
                  <ErrorBoundary>
                    <OpoAdminPage />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />

            {/* Login page route (no authentication required) */}
            <Route
              path="/login"
              element={
                <ErrorBoundary>
                  <LoginPage />
                </ErrorBoundary>
              }
            />

            {/* External case request form route (no authentication required) */}
            <Route
              path="/external-form/:token"
              element={
                <ErrorBoundary>
                  <ExternalCaseRequestForm />
                </ErrorBoundary>
              }
            />

            {/* Catch-all route for debugging */}
            <Route
              path="*"
              element={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="bg-white p-8 rounded-lg shadow-sm max-w-md w-full">
                    <div className="text-center">
                      <div className="text-red-600 text-lg mb-4">404 - Page Not Found</div>
                      <p className="text-gray-600 mb-4">The requested page does not exist.</p>
                      <button
                        onClick={() => window.location.href = '/'}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Back to Home
                      </button>
                    </div>
                  </div>
                </div>
              }
            />
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

// Wrapper component that provides the ModalContext and AuthProvider
function App() {
  // Walkthrough C5. The badge in the chrome only helps once you are already
  // looking at the page; the complaint was several tabs open on the same app, and
  // the tab strip shows the title. public/index.html ships the bare production
  // title, so this only ever adds a suffix.
  useEffect(() => {
    document.title = getDocumentTitle();
  }, []);

  return (
    <AuthProvider>
      <ModalProvider>
        <AppContent />
      </ModalProvider>
    </AuthProvider>
  );
}

export default App;
